import { OverlayViewF } from '@react-google-maps/api'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'

import { getPinColor } from 'tripPlanner/utils/tripItemPin'
import type { BaseMapElementComponentProps } from '../utils'
import { getPathBounds } from '../utils'
import { flightDirection, shortestPathPoints } from './flightPathUtils'
import MapPath from './MapPath'
import CircularPin from './Pins/CircularPin'

import Clickable from 'components/Common/Clickable'
import useStableObjectReference from 'hooks/useStableObjectReference'
import { TripMapContext } from 'tripPlanner/contexts/TripMapContext'
import { ItemMetadata } from 'tripPlanner/models/index'
import type { RouteItem } from 'tripPlanner/types/tripItem'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

interface Props extends BaseMapElementComponentProps {
  item: RouteItem
  map?: google.maps.Map
  isZooming: boolean
}

function TripItemMapRoute(props: Props) {
  const { active, item, zIndex, map } = props

  const pinColor = useMemo(() => getPinColor(item, active), [item, active])

  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)
  const { startPlace, endPlace } = props.item
  const Icon = ItemMetadata[item.type].icon

  const [shortStart, shortEnd] = useStableObjectReference(
    shortestPathPoints(startPlace.point, endPlace.point),
  )

  const bounds = getPathBounds(shortStart, shortEnd)

  const refHandler: React.LegacyRef<HTMLDivElement> = (ref) => {
    ref?.parentElement?.parentElement?.classList.add('trip-map-route-container') // class imported to CP global styles
    if (ref && google?.maps) {
      google.maps.OverlayView.preventMapHitsFrom(ref)
    }
  }

  useEffect(() => {
    if (map) {
      const idleListener = map.addListener('idle', () => {
        const zoom = map.getZoom()
        const projection = map.getProjection()
        if (projection && zoom) {
          const scale = 1 << zoom
          const startPixelCoords = projection.fromLatLngToPoint(shortStart)
          const endPixelCoords = projection.fromLatLngToPoint(shortEnd)
          if (startPixelCoords && endPixelCoords) {
            const width =
              Math.abs(endPixelCoords.x - startPixelCoords.x) * scale
            const height =
              Math.abs(endPixelCoords.y - startPixelCoords.y) * scale
            setWidth(width)
            setHeight(height)
          }
        }
      })

      return () => idleListener.remove()
    }
  }, [map, shortEnd, shortStart])

  const { onSelectMapItem, onDeselectMapItem } = useContext(TripMapContext)

  const onClick = useCallback(() => {
    if (active) {
      onDeselectMapItem()
    } else {
      onSelectMapItem(tripItemSelectionId(item.id))
    }
  }, [active, onDeselectMapItem, onSelectMapItem, item.id])

  useEffect(() => {
    if (active && map) {
      map.fitBounds(bounds)
    }
    // Making bounds a dependency here will keep it locked every time you try and move around on the map
    // Must exclude it to allow user to still freely roam the map
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [active, map])

  if (!map?.getProjection()?.fromLatLngToPoint) return null

  const direction = flightDirection(shortStart, shortEnd)

  return (
    <OverlayViewF mapPaneName="overlayMouseTarget" bounds={bounds}>
      <Container ref={refHandler} style={{ zIndex: active ? 999 : zIndex }}>
        <MapPath
          active={props.active}
          onClick={onClick}
          pxWidth={width}
          pxHeight={height}
          direction={direction}
          marker={
            <div>
              <Clickable onClick={onClick}>
                <CircularPin isActive={active} pinColour={pinColor}>
                  <Icon colour="neutral-eight" />
                </CircularPin>
              </Clickable>
            </div>
          }
        />
      </Container>
    </OverlayViewF>
  )
}

export default React.memo(TripItemMapRoute)
